import { ref, onMounted, onUnmounted } from "vue";
import { auth, db } from "@/firebase";
import {
  collection,
  query,
  where,
  onSnapshot,
  orderBy,
  limit,
  getDocs,
  getDoc,
  doc,
} from "firebase/firestore";
import type { Unsubscribe } from "firebase/firestore";
import { logDebug, logInfo, logError, logWarn } from "@/utils/secureLogger";
import { pendingPostTestService } from "@/services/pendingPostTestService";

export interface CompletedSession {
  sessionId: string;
  subject: string;
  tutorId: string;
  tutorName?: string; // Optional tutor name for display
  startAt: any;
  endAt: any;
  completedAt: any;
  isMandatory?: boolean; // NEW: For pending tests with deadline passed
  deadline?: any; // NEW: Deadline timestamp
}

export function usePostTestDetection() {
  const showPostTestModal = ref(false);
  const completedSession = ref<CompletedSession | null>(null);
  const hasShownModalForSession = ref<Set<string>>(new Set());

  let unsubscribe: Unsubscribe | null = null;

  const processSession = async (sessionId: string, sessionData: any) => {
    // ✅ CRITICAL FIX: Never show post-test for cancelled or rescheduled sessions
    if (
      sessionData.sessionStatus === "cancelled" ||
      sessionData.cancelledAt ||
      sessionData.rescheduleReason
    ) {
      logInfo("Skipping cancelled/rescheduled session - no post-test required", {
        sessionId,
        status: sessionData.sessionStatus,
        reason: sessionData.cancellationReason || sessionData.rescheduleReason,
      });
      // Mark as processed so we don't try again
      hasShownModalForSession.value.add(sessionId);
      const shownSessions = JSON.parse(localStorage.getItem("postTestShownSessions") || "[]");
      shownSessions.push(sessionId);
      localStorage.setItem("postTestShownSessions", JSON.stringify(shownSessions));
      return;
    }

    // Check if we've already shown modal for this session
    if (!hasShownModalForSession.value.has(sessionId)) {
      logInfo("New completed session detected, checking for pending tests first", {
        sessionId,
        subject: sessionData.subject,
      });

      // ✅ FIX: Check if there's already a pending post-test
      // If yes, don't show PostTestModal immediately - let PendingPostTestBanner show first
      const currentUser = auth.currentUser;
      if (currentUser) {
        try {
          const pendingTest = await pendingPostTestService.getNextPendingPostTest(currentUser.uid);

          if (pendingTest) {
            logInfo("Pending post-test found, skipping immediate PostTestModal", {
              pendingSessionId: pendingTest.sessionId,
              newSessionId: sessionId,
            });

            // Mark this session as "processed" so we don't show modal later
            hasShownModalForSession.value.add(sessionId);

            // Store in localStorage to persist across page refreshes
            const shownSessions = JSON.parse(localStorage.getItem("postTestShownSessions") || "[]");
            shownSessions.push(sessionId);
            localStorage.setItem("postTestShownSessions", JSON.stringify(shownSessions));

            // PendingPostTestBanner will show on relevant pages
            return;
          }
        } catch (error) {
          logError("Error checking for pending tests", error);
          // Continue to show PostTestModal if check fails
        }
      }

      // No pending test found, fetch tutor name and show PostTestModal
      let tutorName = "Tutor"; // Default fallback

      // Fetch tutor name from users collection
      if (sessionData.tutorId) {
        try {
          const tutorDoc = await getDoc(doc(db, "users", sessionData.tutorId));
          if (tutorDoc.exists()) {
            const tutorData = tutorDoc.data();
            tutorName =
              `${tutorData.firstName || ""} ${tutorData.lastName || ""}`.trim() || "Tutor";
          }
        } catch (error) {
          logError("Error fetching tutor name", error);
          // Continue with default "Tutor" if fetch fails
        }
      }

      completedSession.value = {
        sessionId,
        subject: sessionData.subject || "General",
        tutorId: sessionData.tutorId || "",
        tutorName: tutorName, // ✅ Now includes fetched tutor name
        startAt: sessionData.startAt,
        endAt: sessionData.endAt,
        completedAt: sessionData.updatedAt || sessionData.createdAt,
      };

      showPostTestModal.value = true;
      hasShownModalForSession.value.add(sessionId);

      // Store in localStorage to persist across page refreshes
      const shownSessions = JSON.parse(localStorage.getItem("postTestShownSessions") || "[]");
      shownSessions.push(sessionId);
      localStorage.setItem("postTestShownSessions", JSON.stringify(shownSessions));
    }
  };

  const startListeningWithIndex = () => {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      logDebug("No authenticated user, skipping post-test detection");
      return;
    }

    logInfo("Starting post-test detection with indexed query");

    const sessionsRef = collection(db, "sessions");

    // Try the indexed query first
    const q = query(
      sessionsRef,
      where("studentId", "==", currentUser.uid),
      where("sessionStatus", "==", "completed"),
      where("status", "==", "active"),
      orderBy("updatedAt", "desc"),
      limit(1)
    );

    unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        snapshot.docChanges().forEach(async (change) => {
          if (change.type === "added" || change.type === "modified") {
            const sessionData = change.doc.data();
            const sessionId = change.doc.id;
            await processSession(sessionId, sessionData);
          }
        });
      },
      (error) => {
        logError("Error listening for completed sessions", error);
        // If index error, try fallback
        if (error.code === "failed-precondition" || error.message.includes("index")) {
          logWarn("Index not available, switching to fallback query");
          stopListening();
          startListeningWithoutIndex();
        }
      }
    );
  };

  const startListeningWithoutIndex = () => {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      logDebug("No authenticated user, skipping post-test detection");
      return;
    }

    logInfo("Starting post-test detection with fallback query (no index)");

    const sessionsRef = collection(db, "sessions");

    // Simpler query without orderBy - doesn't require composite index
    const q = query(
      sessionsRef,
      where("studentId", "==", currentUser.uid),
      where("sessionStatus", "==", "completed"),
      limit(10) // Get more to filter manually
    );

    unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        // ✅ FIX: Process all docChanges to catch newly added sessions
        snapshot.docChanges().forEach(async (change) => {
          if (change.type === "added" || change.type === "modified") {
            const sessionData = change.doc.data();
            const sessionId = change.doc.id;

            // Only process active sessions
            if (sessionData.status === "active") {
              logDebug("Processing session from docChanges", {
                sessionId,
                changeType: change.type,
                subject: sessionData.subject,
              });
              await processSession(sessionId, sessionData);
            }
          }
        });
      },
      (error) => {
        logError("Error with fallback query", error);
      }
    );
  };

  const startListening = () => {
    try {
      startListeningWithIndex();
    } catch (error) {
      logWarn("Failed to start indexed query, using fallback", error);
      startListeningWithoutIndex();
    }
  };

  const stopListening = () => {
    if (unsubscribe) {
      unsubscribe();
      unsubscribe = null;
      logDebug("Stopped post-test detection listener");
    }
  };

  const closeModal = () => {
    showPostTestModal.value = false;
    completedSession.value = null;
  };

  onMounted(() => {
    // Load previously shown sessions from localStorage
    try {
      const shownSessions = JSON.parse(localStorage.getItem("postTestShownSessions") || "[]");
      hasShownModalForSession.value = new Set(shownSessions);
      logDebug("Loaded shown post-test sessions from localStorage", {
        count: shownSessions.length,
      });
    } catch (error) {
      logError("Error loading shown sessions from localStorage", error);
    }

    // Start listening after a short delay to ensure Firebase is ready
    setTimeout(startListening, 1000);
  });

  onUnmounted(() => {
    stopListening();
  });

  return {
    showPostTestModal,
    completedSession,
    closeModal,
    startListening,
    stopListening,
  };
}
